import { useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../api";
import { AuthError, AuthField, AuthShell } from "../components/AuthShell";

const ROLE_LABEL: Record<string, string> = {
  owner: "an owner",
  viewer: "a viewer",
  admin: "an admin",
  dev: "a developer",
};

/** /invite#<token>: choose a username and password for an invited role. */
export default function AcceptInvite() {
  const nav = useNavigate();
  const token = decodeURIComponent(window.location.hash.replace(/^#/, ""));
  const info = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.invites.info(token),
    enabled: token.length > 0,
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      await api.invites.accept(token, username.trim(), password);
      nav({ to: "/" });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not accept the invitation.");
    } finally {
      setLoading(false);
    }
  }

  if (!token || info.isError) {
    return (
      <AuthShell
        title="Invitation not valid"
        subtitle="This link has expired or was already used. Ask for a new one."
        footer={
          <Link to="/login" className="font-medium text-brand-fuchsia hover:underline">
            Go to sign in
          </Link>
        }
      >
        <span />
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={info.data ? `Join ${info.data.business_name}` : "Join IntraScene"}
      subtitle={
        info.data
          ? `You are invited as ${ROLE_LABEL[info.data.role] ?? info.data.role}. Choose how you sign in.`
          : "Checking your invitation…"
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <AuthField
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          pattern="[A-Za-z0-9._@+\-]{2,64}"
          title="Letters, numbers and . _ @ + -"
          autoComplete="username"
          autoFocus
          required
        />
        <AuthField
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength={10}
          autoComplete="new-password"
          hint="At least 10 characters."
          required
        />
        <AuthError message={err} />
        <button className="bf-button w-full py-2.5" disabled={loading || !info.data} type="submit">
          {loading ? "Joining..." : "Join"}
        </button>
      </form>
    </AuthShell>
  );
}
